import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Badge,
  Button,
  Avatar,
  AvatarFallback,
  Separator,
  Textarea,
  useToast,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  User,
  Building2,
  CheckCircle2,
  Circle,
  Calendar,
  Pencil,
  Pill,
  Trash2,
  MessageSquare,
  Send,
  Loader2,
  X,
  Check,
  MessageSquarePlus,
  Repeat,
  UserCircle,
} from "lucide-react";
import { formatRelativeTime } from "@repo/utils";
import { useAuth } from "@/lib/auth/auth-provider";
import { getNotes, createNote, updateNote, deleteNote } from "@/lib/api/notes";
import { ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { EntityReferenceCard } from "./entity-reference-badge";
import {
  getPriorityColor,
  getPriorityLabel,
  getRecurrenceLabel,
} from "@/lib/utils/reminder-format";
import type { Reminder } from "@/hooks/use-reminders";
import type { Note } from "@/lib/types/inventory";

interface ReminderDetailPanelProps {
  reminder: Reminder | null;
  open: boolean;
  onClose: () => void;
  onEdit: (reminder: Reminder) => void;
  onDelete: (id: string) => void;
  onToggleComplete: (id: string) => void;
}

export function ReminderDetailPanel({
  reminder,
  open,
  onClose,
  onEdit,
  onDelete,
  onToggleComplete,
}: ReminderDetailPanelProps) {
  const auth = useAuth();
  if (!reminder) return null;

  const isCreator = auth.user?.id === reminder.created_by;
  const canEdit = isCreator || reminder.visibility === "pharmacy";
  const canDelete =
    isCreator ||
    auth.user?.role === "admin" ||
    auth.user?.role === "supervising_pharmacist";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0">
        <div className="overflow-y-auto flex-1">
          <div className="p-6 pb-0">
            <SheetHeader className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3">
                  <button
                    className="mt-1 shrink-0"
                    onClick={() => onToggleComplete(reminder.id)}
                  >
                    {reminder.is_completed ? (
                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                    ) : (
                      <Circle className="h-6 w-6 text-muted-foreground hover:text-primary transition-colors" />
                    )}
                  </button>
                  <SheetTitle
                    className={
                      reminder.is_completed
                        ? "line-through text-muted-foreground"
                        : ""
                    }
                  >
                    {reminder.title}
                  </SheetTitle>
                </div>
              </div>
              <SheetDescription className="sr-only">
                Reminder details
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-5">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="gap-1.5">
                  <span
                    className={`h-2 w-2 rounded-full ${getPriorityColor(reminder.priority)}`}
                  />
                  {getPriorityLabel(reminder.priority)}
                </Badge>

                <Badge variant="outline" className="gap-1.5">
                  {reminder.visibility === "pharmacy" ? (
                    <Building2 className="h-3 w-3" />
                  ) : (
                    <User className="h-3 w-3" />
                  )}
                  {reminder.visibility === "pharmacy" ? "Pharmacy" : "Personal"}
                </Badge>

                <Badge variant="outline" className="gap-1.5">
                  <Calendar className="h-3 w-3" />
                  {new Date(reminder.due_date + "T00:00:00").toLocaleDateString(
                    "en-US",
                    {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }
                  )}
                  {reminder.due_time && ` at ${reminder.due_time.slice(0, 5)}`}
                </Badge>
              </div>

              {/* Recurrence info */}
              {reminder.recurrence_type &&
                reminder.recurrence_type !== "none" && (
                  <div className="space-y-1.5">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Repeat className="h-4 w-4 text-muted-foreground" />
                      Recurring
                    </h4>
                    <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
                      <p className="text-sm">
                        Repeats{" "}
                        {getRecurrenceLabel(
                          reminder.recurrence_type,
                          reminder.recurrence_interval
                        )?.toLowerCase()}
                      </p>
                      {reminder.recurrence_end_date && (
                        <p className="text-xs text-muted-foreground">
                          Until{" "}
                          {new Date(
                            reminder.recurrence_end_date + "T00:00:00"
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                )}

              {/* Description */}
              {reminder.description && (
                <div className="space-y-1.5">
                  <h4 className="text-sm font-medium">Description</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {reminder.description}
                  </p>
                </div>
              )}

              {/* Entity reference - rich card */}
              {reminder.reference_type && reminder.reference_id && (
                <div className="space-y-1.5">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Pill className="h-4 w-4 text-muted-foreground" />
                    {reminder.reference_type === "inventory"
                      ? "Linked Drug"
                      : "Linked Item"}
                  </h4>
                  <EntityReferenceCard
                    referenceType={reminder.reference_type}
                    referenceId={reminder.reference_id}
                  />
                </div>
              )}

              {/* Linked patient - independent from entity reference */}
              {reminder.patient_id && reminder.patient_name && (
                <div className="space-y-1.5">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <UserCircle className="h-4 w-4 text-muted-foreground" />
                    Linked Patient
                  </h4>
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <UserCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {reminder.patient_name}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Completion info */}
              {reminder.is_completed && reminder.completed_by_user && (
                <div className="space-y-1.5">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Completed
                  </h4>
                  <div className="rounded-lg border bg-green-50 dark:bg-green-950/20 p-3 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Avatar className="h-5 w-5">
                        <AvatarFallback className="text-[9px]">
                          {reminder.completed_by_user.initials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">
                        {reminder.completed_by_user.full_name}
                      </span>
                      {reminder.completed_at && (
                        <span className="text-muted-foreground">
                          {formatRelativeTime(reminder.completed_at)}
                        </span>
                      )}
                    </div>
                    {reminder.completion_note && (
                      <p className="text-sm text-muted-foreground pl-7">
                        {reminder.completion_note}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Created by */}
              <div className="space-y-1.5">
                <h4 className="text-sm font-medium">Created By</h4>
                {reminder.created_by_user && (
                  <div className="flex items-center gap-2 text-sm">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-[9px]">
                        {reminder.created_by_user.initials}
                      </AvatarFallback>
                    </Avatar>
                    <span>{reminder.created_by_user.full_name}</span>
                    <span className="text-muted-foreground">
                      {formatRelativeTime(reminder.created_at)}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => onEdit(reminder)}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => onToggleComplete(reminder.id)}
                >
                  {reminder.is_completed ? (
                    <>
                      <Circle className="h-4 w-4" />
                      Mark Incomplete
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Mark Complete
                    </>
                  )}
                </Button>
                {canDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-destructive hover:text-destructive"
                    onClick={() => onDelete(reminder.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                )}
              </div>

              <Separator />
            </div>
          </div>

          {/* Inline Notes Thread */}
          <div className="p-6 pt-4">
            <InlineNotesThread reminderId={reminder.id} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Inline Notes Thread ─────────────────────────────────────────────────────

function InlineNotesThread({ reminderId }: { reminderId: string }) {
  const [newNote, setNewNote] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [editingNoteId, setEditingNoteId] = React.useState<string | null>(null);
  const [editContent, setEditContent] = React.useState("");
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [deleteNoteId, setDeleteNoteId] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const activeUserId = auth.user?.id ?? null;
  const userRole = auth.user?.role ?? null;

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["reminderNotes", reminderId],
    queryFn: async () => {
      try {
        const data = await getNotes({
          entityType: "reminder",
          entityId: reminderId,
        });
        return (data.data || []) as Note[];
      } catch {
        return [];
      }
    },
    enabled: !!reminderId,
  });

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setIsSubmitting(true);
    try {
      const data = await createNote({
        entityType: "reminder",
        entityId: reminderId,
        content: newNote.trim(),
      });
      queryClient.setQueryData<Note[]>(
        ["reminderNotes", reminderId],
        (prev) => [data.data, ...(prev || [])]
      );
      setNewNote("");
      toast({ title: "Note added", variant: "success" });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {},
          actionDescription: "Add note",
        })
      )
        return;
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingNoteId || !editContent.trim()) return;
    setIsUpdating(true);
    try {
      const data = await updateNote(editingNoteId, {
        content: editContent.trim(),
      });
      queryClient.setQueryData<Note[]>(["reminderNotes", reminderId], (prev) =>
        (prev || []).map((n) => (n.id === editingNoteId ? data.data : n))
      );
      setEditingNoteId(null);
      setEditContent("");
      toast({ title: "Note updated", variant: "success" });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {},
          actionDescription: "Update note",
        })
      )
        return;
      toast({
        title: "Error",
        description: "Failed to update note",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!deleteNoteId) return;
    setIsDeleting(true);
    try {
      await deleteNote(deleteNoteId);
      queryClient.setQueryData<Note[]>(["reminderNotes", reminderId], (prev) =>
        (prev || []).filter((n) => n.id !== deleteNoteId)
      );
      setDeleteNoteId(null);
      toast({ title: "Note deleted", variant: "success" });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {},
          actionDescription: "Delete note",
        })
      )
        return;
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const canModifyNote = (note: Note) => {
    if (!activeUserId) return false;
    if (note.created_by === activeUserId) return true;
    if (userRole && ["admin", "supervising_pharmacist"].includes(userRole))
      return true;
    return false;
  };

  return (
    <>
      <div className="space-y-4">
        {/* Section header */}
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">
            Notes{notes.length > 0 && ` (${notes.length})`}
          </h4>
        </div>

        {/* Add note input */}
        <div className="flex gap-2">
          <Textarea
            placeholder="Write a note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={2}
            className="resize-none text-sm flex-1"
            disabled={isSubmitting}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleAddNote();
              }
            }}
          />
          <Button
            size="sm"
            className="shrink-0 self-end gap-1.5 px-3"
            onClick={handleAddNote}
            disabled={isSubmitting || !newNote.trim()}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send
          </Button>
        </div>

        {/* Notes list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <MessageSquarePlus className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground">
              No notes yet. Add one above.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div
                key={note.id}
                className="group rounded-lg border p-3 space-y-2 transition-colors hover:bg-muted/30"
              >
                {/* Note header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                        {note.created_by_user?.initials || "??"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-xs font-medium leading-none">
                        {note.created_by_user?.full_name || "Unknown"}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {formatRelativeTime(note.created_at)}
                        {note.updated_at !== note.created_at && (
                          <span className="ml-1 italic">(edited)</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {canModifyNote(note) && editingNoteId !== note.id && (
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setEditingNoteId(note.id);
                          setEditContent(note.content);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => setDeleteNoteId(note.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Note content */}
                {editingNoteId === note.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={2}
                      className="resize-none text-sm"
                      disabled={isUpdating}
                      autoFocus
                    />
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditContent("");
                        }}
                        disabled={isUpdating}
                      >
                        <X className="mr-1 h-3 w-3" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleSaveEdit}
                        disabled={isUpdating || !editContent.trim()}
                      >
                        {isUpdating ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <Check className="mr-1 h-3 w-3" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-foreground whitespace-pre-wrap pl-8">
                    {note.content}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteNoteId}
        onOpenChange={(o) => !o && setDeleteNoteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
